import ExcelJS from 'exceljs';
import Button from '@mui/material/Button';
import { useTranslation } from 'react-i18next';
import { SURVEY_MAPPING, COHORT_DATE_COUNT } from '../Constants';

function downloadBlob(blob: Blob, filename: string) {
  const url = URL.createObjectURL(blob);
  const a = document.createElement('a');
  a.href = url;
  a.download = filename;
  document.body.appendChild(a);
  a.click();
  a.remove();
  URL.revokeObjectURL(url);
}

type ExcelTemplateButtonProps = {
  mode: 'surveys' | 'teams';
};

export default function ExcelTemplateButton({ mode }: ExcelTemplateButtonProps) {
  const { t } = useTranslation();
  const handleDownload = async () => {
    const workbook = new ExcelJS.Workbook();
    const sheet = workbook.addWorksheet('Template');

    if (mode === 'surveys') {
      sheet.columns = [
        { header: 'Team name', key: 'teamName', width: 28 },
        { header: 'Report type', key: 'reportType', width: 22 },
        { header: 'Leader mail address', key: 'leaderEmail', width: 30 },
        { header: 'Reminder date', key: 'reminderDate', width: 26 },
        { header: 'Report generation date', key: 'reportDate', width: 26 },
      ];

      sheet.getRow(1).font = { bold: true };

      sheet.addRow({
        teamName: 'Team Rocket',
        reportType: 'Assessment',
        leaderEmail: 'giovanni@teamrocket.com, jessie@teamrocket.com',
        reminderDate: '1996-02-24',
        reportDate: '1996-02-27',
      });

      const START_ROW = 2;
      const END_ROW = 500;
      const reportTypeList = '"' + Object.keys(SURVEY_MAPPING).join(',') + '"';
      for (let r = START_ROW; r <= END_ROW; r++) {
        sheet.getCell(`B${r}`).dataValidation = {
          type: 'list',
          allowBlank: true,
          formulae: [reportTypeList],
          showErrorMessage: true,
          errorStyle: 'stop',
          errorTitle: 'Invalid report type',
          error: 'Choose a value from the dropdown: ' + Object.keys(SURVEY_MAPPING).join(', '),
        };
      }

      sheet.views = [{ state: 'frozen', ySplit: 1 }];

      const buffer = await workbook.xlsx.writeBuffer();
      downloadBlob(
        new Blob([buffer], {
          type: 'application/vnd.openxmlformats-officedocument.spreadsheetml.sheet',
        }),
        'report-template.xlsx'
      );
    } else {
      const dateColumns = Array.from({ length: COHORT_DATE_COUNT }, (_, i) => ({
        header: t(`cohorts.date_default_name_${i + 1}`),
        key: `date${i + 1}`,
        width: 20,
      }));
      sheet.columns = [
        { header: 'Team name', key: 'teamName', width: 28 },
        { header: 'Leader email', key: 'leaderEmail', width: 30 },
        { header: 'Cohort name', key: 'cohortName', width: 28 },
        { header: 'System leader email', key: 'systemLeaderEmail', width: 30 },
        ...dateColumns,
      ];

      sheet.getRow(1).font = { bold: true };

      const exampleDates = [
        '2026-06-01',
        '2026-09-01',
        '2026-12-01',
        '2027-03-01',
        '2027-06-01',
        '2027-09-01',
      ];
      const exampleDateFields = Object.fromEntries(
        Array.from({ length: COHORT_DATE_COUNT }, (_, i) => [`date${i + 1}`, exampleDates[i] ?? ''])
      );
      const emptyDateFields = Object.fromEntries(
        Array.from({ length: COHORT_DATE_COUNT }, (_, i) => [`date${i + 1}`, ''])
      );

      sheet.addRow({
        teamName: 'Team Rocket',
        leaderEmail: 'giovanni@teamrocket.com, james@teamrocket.com',
        cohortName: 'Cohort Alpha',
        systemLeaderEmail: 'jessie@teamrocket.com',
        ...exampleDateFields,
      });
      sheet.addRow({
        teamName: 'Team Magma',
        leaderEmail: 'maxie@teammagma.com',
        cohortName: 'Cohort Alpha',
        systemLeaderEmail: 'jessie@teamrocket.com',
        ...exampleDateFields,
      });
      sheet.addRow({
        teamName: 'Team Aqua',
        leaderEmail: 'archie@teamaqua.com',
        cohortName: '',
        systemLeaderEmail: '',
        ...emptyDateFields,
      });

      sheet.views = [{ state: 'frozen', ySplit: 1 }];

      const buffer = await workbook.xlsx.writeBuffer();
      downloadBlob(
        new Blob([buffer], {
          type: 'application/vnd.openxmlformats-officedocument.spreadsheetml.sheet',
        }),
        'teams-template.xlsx'
      );
    }
  };

  return (
    <Button variant="outlined" onClick={() => handleDownload()}>
      Download Excel Template
    </Button>
  );
}
